import React, { useState } from "react";
import { Spinner, Flex, Grid } from "@theme-ui/components";
import ReCAPTCHA from "react-google-recaptcha";
import queryString from "query-string";
import PropType from "prop-types";
import { navigate } from "gatsby";
import _ from "lodash";
import { ActionBanner } from "../molecules";
import { validate, subscribeV1 } from "@greythr/shared-utils/hsAPI";
import { getButtonId } from "@greythr/shared-utils/common";
import { Button, InputBox } from "../atoms";
import { colors } from "../../styles/theme";

const SERVER_ERROR =
  "Sorry, some error seems to have occurred and we could not complete your request. Please try again.";

let recaptchaRef = React.createRef();

const Input = ({
  label,
  name,
  value,
  maxlength,
  error,
  onChange,
  ...props
}) => (
  <InputBox
    label={label}
    isRequired={true}
    labelColor={colors.white}
    errorColor={colors.white}
    name={name}
    value={value}
    maxlength={maxlength}
    customStyles={{
      width: "28rem",
      "@media(max-width: 1130px)": {
        width: "18.75rem",
      },
      "@media(max-width: 700px)": {
        width: "16rem",
      },
    }}
    error={error}
    onChange={onChange}
    {...props}
  />
);

const Form = ({
  formCode,
  pageCode,
  pageName,
  pageUrl,
  regionCode,
  searchParams,
  setHideForm,
  setIsSuccess,
}) => {
  const utm_source = searchParams["utm_source"];
  const utm_medium = searchParams["utm_medium"];
  const utm_campaign = searchParams["utm_campaign"];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [error, setError] = useState({});
  const [processing, setProcessing] = useState(false);

  const validateForm = () => {
    let error = {};
    if (email) {
      const emailErr = validate(email);
      emailErr && (error["email"] = emailErr);
    }
    return error;
  };

  const handleClick = async (e) => {
    e.preventDefault();
    setError({});
    setProcessing(true);

    const formErrors = validateForm();
    if (!_.isEmpty(formErrors)) {
      setError(formErrors);
      setProcessing(false);
      return;
    }

    if (recaptchaRef && recaptchaRef.current) {
      let captcha = "";
      recaptchaRef.current.reset();
      captcha = await recaptchaRef.current.executeAsync();
      const payload = {
        email,
        full_name: name,
        form_name: formCode,
        page_code: pageCode,
        page_name: pageName,
        page_url: pageUrl,
        utm_source,
        utm_medium,
        utm_campaign,
        captcha,
        invisible_recaptcha: true,
      };
      if (captcha) {
        subscribeV1(
          payload,
          (response) => {
            setProcessing(false);
            setHideForm(true);
            setIsSuccess(true);
          },
          (error) => {
            setProcessing(false);
            if (
              error.response &&
              error.response.status == 400 &&
              error.response.data.invalid_email
            ) {
              setError((prev) => ({
                ...prev,
                email: error.response.data.invalid_email,
              }));
            } else {
              setHideForm(true);
              setIsSuccess(false);
            }
          },
        );
      } else {
        setProcessing(false);
        setHideForm(true);
        setIsSuccess(false);
      }
    } else {
      setProcessing(false);
      setHideForm(true);
      setIsSuccess(false);
    }
  };

  return (
    <Flex
      className="glass50"
      sx={styles.container}
      as="form"
      onSubmit={handleClick}
    >
      <Grid sx={styles.subContainer} columns={[1, 1, 2, 2]}>
        <Input
          type="text"
          label="Full Name"
          value={name}
          name="name"
          maxlength={100}
          error={error?.name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label="Business Email"
          name="email"
          value={email}
          maxlength={100}
          error={error?.email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Grid>

      {processing ? (
        <Spinner color="white" size={58} />
      ) : (
        <Button
          id={getButtonId(`${pageCode}_mailing_subscription`, regionCode)}
          variant="outlined"
          size="large"
          disabled={!email || !name}
        >
          Join Now
        </Button>
      )}
    </Flex>
  );
};

const MailingSubscriptionForm = ({
  formCode,
  pageCode,
  pageName,
  pageUrl,
  regionCode,
  search,
}) => {
  const searchParams = queryString.parse(search);
  const [hideForm, setHideForm] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const refresh = () => {
    setHideForm(false);
    setIsSuccess(false);
    navigate("#");
  };

  return (
    <Flex id="cta-block">
      {hideForm ? (
        <ActionBanner
          theme="primary"
          variant="BodyBannerV1"
          title={
            isSuccess ? (
              <>
                Thank you for joining our mailing list.
                <br />
                You will hear from us soon.
              </>
            ) : (
              SERVER_ERROR
            )
          }
          subTitle=""
          customStyles={{ minHeight: "44.5rem", width: "100%" }}
        >
          {!isSuccess && (
            <Button variant="outlined" size="large" onClick={refresh}>
              Refresh
            </Button>
          )}
        </ActionBanner>
      ) : (
        <ActionBanner
          theme="primary"
          variant="BodyBannerV1"
          title={
            <>
              Be the first to know about our webinars.
              <br />
              Join our mailing list.
            </>
          }
          subTitle=""
          customStyles={{ width: "100%" }}
        >
          <Form
            formCode={formCode}
            pageUrl={pageUrl}
            setHideForm={setHideForm}
            setIsSuccess={setIsSuccess}
            pageCode={pageCode}
            pageName={pageName}
            regionCode={regionCode}
            searchParams={searchParams}
          />
        </ActionBanner>
      )}
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={process.env.GATSBY_INVISIBLE_RE_CAPTCHA_SITE_KEY}
        badge="bottomleft"
      />
    </Flex>
  );
};

const styles = {
  container: {
    borderRadius: "16px",
    p: ["1.5rem", "1.5rem", "1.5rem", "2.5rem"],
    border: "1px solid rgba(232, 232, 233, 0.50)",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  subContainer: {
    columnGap: "1.5rem",
    rowGap: "1rem",
    px: [0, 0, 0, "2.5rem"],
    mb: "3rem",
  },
};

MailingSubscriptionForm.propTypes = {
  pageCode: PropType.string,
  pageName: PropType.string,
  pageUrl: PropType.string,
  regionCode: PropType.string,
  search: PropType.any,
};

Form.propTypes = {
  pageCode: PropType.string,
  pageName: PropType.string,
  pageUrl: PropType.string,
  regionCode: PropType.string,
  searchParams: PropType.object,
  setHideForm: PropType.func,
  setIsSuccess: PropType.func,
};

Input.propTypes = {
  label: PropType.string,
  name: PropType.string,
  value: PropType.string,
  maxlength: PropType.number,
  error: PropType.string,
  onChange: PropType.func,
};

export default MailingSubscriptionForm;
